import {
  AUTO_FAIL,
  CRITERIA,
  PASS_AT,
  type Criterion,
  type VerdictCriterion,
} from './evaluation-rubric';

/**
 * Judges — LLM-as-judge set up per solution, the way the scorer services in
 * AI Observability do it (`llm_judge_pipeline`, the HBO Max and Sling
 * scorers): a customer's own categories with fixed allowed scores, rules for
 * its situations, worked examples with the score the team gave them, a
 * guardrail judge that passes unless something is plainly wrong, and topic
 * tagging.
 *
 * Every solution starts with the `rubric` judge — the Generative AI
 * Evaluation Rubric, which is what passes or fails a run. Its rules,
 * examples, model and pass bar can be edited; its criteria can't. Other
 * judges score alongside it.
 *
 * Pure data and checks, with no imports beyond the rubric: the server's
 * judge prompts (`server-only/ai/judges.ts`) and the Validate page both read
 * it. `sanitizeJudgeConfig` is the one gate a judge passes through, whether
 * it comes from the browser's storage or over the wire.
 */

export type JudgeKind = 'rubric' | 'score' | 'check' | 'tag';

/**
 * A scored category with the only scores it may give (e.g. 0, 15 or 30).
 * On the Quality score judge it's one of the rubric's criteria instead:
 * scored 1–5 against `levels`, worth `weight` of the 100 points.
 */
export interface ScoreCriterion {
  key: string;
  label: string;
  group: string;
  definition: string;
  scores: number[];
  /** Rubric only: the short label, for meters and the breakdown. */
  short?: string;
  /** Rubric only: what levels 1 to 5 mean, level 1 first. */
  levels?: string[];
  /** Rubric only: its points out of 100. */
  weight?: number;
}

/** One pass-or-fail check: what fails it, and what never does. */
export interface JudgeCheck {
  name: string;
  fails: string[];
  never: string[];
}

export interface JudgeTopic {
  name: string;
  subs: string[];
}

export interface ExampleLine {
  who: 'customer' | 'bot';
  text: string;
}

/**
 * What a person says a conversation deserves: a score out of 100 (`rubric`,
 * `score`), "pass" or "fail" (`check`), or "Topic" / "Topic › Subtopic"
 * (`tag`).
 */
export type JudgeExpected = number | string;

export interface JudgeExample {
  id: string;
  title: string;
  lines: ExampleLine[];
  expected: JudgeExpected;
  note: string;
}

/** Everything the judge's prompt is built from — and nothing else. */
export interface JudgeConfig {
  kind: JudgeKind;
  name: string;
  /** `score` only. */
  criteria: ScoreCriterion[];
  /** `check` only. */
  checks: JudgeCheck[];
  /** `tag` only. */
  topics: JudgeTopic[];
  rules: string[];
  examples: JudgeExample[];
  /** Passes at this score out of 100; `rubric` and `score`. */
  bar: number;
  model: JudgeModelId;
}

/** Models a judge can run on, through Pro Studio's own Claude client. */
export const JUDGE_MODELS = [
  {
    id: 'claude-haiku-5-5',
    label: 'Claude Haiku 5.5',
    note: 'What every judge scores with, as the transcript scorers do.',
  },
] as const;

/**
 * Judges saved on another model run on Haiku 5.5: Haiku 4.5 ones moved up,
 * Sonnet ones moved down (judges don't run on Sonnet).
 */
const RETIRED_MODELS: Record<string, JudgeModelId> = {
  'claude-haiku-4-5-20251001': 'claude-haiku-5-5',
  'claude-haiku-4-5': 'claude-haiku-5-5',
  'claude-sonnet-5-5': 'claude-haiku-5-5',
};

export type JudgeModelId = (typeof JUDGE_MODELS)[number]['id'];
export const DEFAULT_JUDGE_MODEL: JudgeModelId = 'claude-haiku-5-5';

export const isJudgeModel = (v: unknown): v is JudgeModelId =>
  JUDGE_MODELS.some((m) => m.id === v);

/* ---- Limits: what one judge can put in a prompt ---------------------------- */

export const JUDGE_LIMITS = {
  name: 80,
  text: 600,
  rules: 30,
  examples: 8,
  exampleLines: 12,
  criteria: 10,
  /** The rubric's: 2 to 10 criteria, each worth 5 to 60 points. */
  rubricCriteria: 10,
  scoresPerCriterion: 6,
  checks: 8,
  checkItems: 15,
  topics: 25,
  subs: 15,
} as const;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const text = (v: unknown, max: number = JUDGE_LIMITS.text): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : '';

const textList = (v: unknown, max: number): string[] =>
  Array.isArray(v)
    ? v
        .map((x) => text(x))
        .filter(Boolean)
        .slice(0, max)
    : [];

const clampBar = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n)
    ? Math.min(100, Math.max(0, Math.round(n)))
    : fallback;
};

const slug = (s: string, i: number) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40) || `c${i + 1}`;

const sanitizeCriterion = (v: unknown, i: number): ScoreCriterion | null => {
  if (!isRecord(v)) return null;
  const label = text(v.label, JUDGE_LIMITS.name);
  const scores = Array.isArray(v.scores)
    ? [
        ...new Set(
          v.scores
            .map((s) => (typeof s === 'number' ? s : Number(s)))
            .filter((s) => Number.isFinite(s) && s >= 0 && s <= 100)
            .map((s) => Math.round(s))
        ),
      ]
        .sort((a, b) => a - b)
        .slice(0, JUDGE_LIMITS.scoresPerCriterion)
    : [];
  if (!label || scores.length < 2) return null;
  return {
    key: text(v.key, 40) || slug(label, i),
    label,
    group: text(v.group, JUDGE_LIMITS.name),
    definition: text(v.definition),
    scores,
  };
};

const LEVELS = [1, 2, 3, 4, 5];

const sanitizeRubricCriterion = (
  v: unknown,
  i: number
): ScoreCriterion | null => {
  if (!isRecord(v)) return null;
  const label = text(v.label, JUDGE_LIMITS.name);
  const definition = text(v.definition);
  if (!label || !definition) return null;
  const raw = Array.isArray(v.levels) ? v.levels : [];
  const levels = LEVELS.map((_, k) => text(raw[k]));
  const weight = Number(v.weight);
  return {
    key: text(v.key, 40) || slug(label, i),
    label,
    short: text(v.short, 24) || label.slice(0, 24),
    group: text(v.group, JUDGE_LIMITS.name),
    definition,
    scores: LEVELS,
    levels,
    weight: Number.isFinite(weight) && weight > 0 ? Math.round(weight) : 0,
  };
};

/**
 * Weights scaled to add up to exactly 100, keeping their proportions — the
 * page asks for 100, but nothing past this point has to trust that it did.
 */
export const toHundred = (criteria: ScoreCriterion[]): ScoreCriterion[] => {
  const given = criteria.map((c) => (c.weight && c.weight > 0 ? c.weight : 1));
  const total = given.reduce((a, b) => a + b, 0);
  if (!total) return criteria;
  const exact = given.map((w) => (w * 100) / total);
  const out = exact.map(Math.floor);
  let left = 100 - out.reduce((a, b) => a + b, 0);
  exact
    .map((w, i) => ({ i, frac: w - Math.floor(w) }))
    .sort((a, b) => b.frac - a.frac)
    .forEach(({ i }) => {
      if (left > 0) {
        out[i] += 1;
        left -= 1;
      }
    });
  return criteria.map((c, i) => ({ ...c, weight: out[i] }));
};

/** Keys made unique, so two criteria can't share a score. */
const uniqueKeys = (criteria: ScoreCriterion[]): ScoreCriterion[] => {
  const seen = new Set<string>();
  return criteria.map((c, i) => {
    let { key } = c;
    while (seen.has(key)) key = `${c.key}_${i + 1}`;
    seen.add(key);
    return key === c.key ? c : { ...c, key };
  });
};

const sanitizeCheck = (v: unknown): JudgeCheck | null => {
  if (!isRecord(v)) return null;
  const name = text(v.name, JUDGE_LIMITS.name);
  if (!name) return null;
  return {
    name,
    fails: textList(v.fails, JUDGE_LIMITS.checkItems),
    never: textList(v.never, JUDGE_LIMITS.checkItems),
  };
};

const sanitizeTopic = (v: unknown): JudgeTopic | null => {
  if (!isRecord(v)) return null;
  const name = text(v.name, JUDGE_LIMITS.name);
  return name
    ? {
        name,
        subs: textList(v.subs, JUDGE_LIMITS.subs).map((s) =>
          s.slice(0, JUDGE_LIMITS.name)
        ),
      }
    : null;
};

const sanitizeExpected = (
  kind: JudgeKind,
  v: unknown
): JudgeExpected | null => {
  if (kind === 'check') {
    const s = text(v, 10).toLowerCase();
    return s === 'pass' || s === 'fail' ? s : null;
  }
  if (kind === 'tag') return text(v, JUDGE_LIMITS.name * 2) || null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : null;
};

const sanitizeExample = (
  kind: JudgeKind,
  v: unknown,
  i: number
): JudgeExample | null => {
  if (!isRecord(v)) return null;
  const expected = sanitizeExpected(kind, v.expected);
  const lines = Array.isArray(v.lines)
    ? v.lines
        .filter(isRecord)
        .map((l) => ({
          who: l.who === 'bot' ? ('bot' as const) : ('customer' as const),
          text: text(l.text),
        }))
        .filter((l) => l.text)
        .slice(0, JUDGE_LIMITS.exampleLines)
    : [];
  if (expected === null || !lines.length) return null;
  return {
    id: text(v.id, 60) || `ex${i + 1}`,
    title: text(v.title, JUDGE_LIMITS.name) || `Example ${i + 1}`,
    lines,
    expected,
    note: text(v.note),
  };
};

/**
 * The rubric's criteria as saved: empty while they're the standard five, so
 * an unedited rubric reads (and hashes) exactly as it always did.
 */
const rubricCriteriaFrom = (raw: unknown): ScoreCriterion[] => {
  const list = Array.isArray(raw)
    ? raw
        .map(sanitizeRubricCriterion)
        .filter((c): c is ScoreCriterion => c !== null)
        .slice(0, JUDGE_LIMITS.rubricCriteria)
    : [];
  if (list.length < 2) return [];
  const criteria = toHundred(uniqueKeys(list));
  return isStandardRubric(criteria) ? [] : criteria;
};

const JUDGE_KINDS: JudgeKind[] = ['rubric', 'score', 'check', 'tag'];

/**
 * A judge's prompt inputs, cleaned and capped — or null when there is nothing
 * a judge could score with (a `score` judge with no criteria, a `check`
 * judge with no checks, a `tag` judge with no topics).
 */
export const sanitizeJudgeConfig = (v: unknown): JudgeConfig | null => {
  if (!isRecord(v) || !JUDGE_KINDS.includes(v.kind as JudgeKind)) return null;
  const kind = v.kind as JudgeKind;
  const list = <T>(
    raw: unknown,
    max: number,
    f: (x: unknown, i: number) => T | null
  ) =>
    Array.isArray(raw)
      ? raw
          .map(f)
          .filter((x): x is T => x !== null)
          .slice(0, max)
      : [];
  const config: JudgeConfig = {
    kind,
    name: text(v.name, JUDGE_LIMITS.name) || 'Judge',
    criteria:
      kind === 'rubric'
        ? rubricCriteriaFrom(v.criteria)
        : kind === 'score'
          ? list(v.criteria, JUDGE_LIMITS.criteria, sanitizeCriterion)
          : [],
    checks:
      kind === 'check'
        ? list(v.checks, JUDGE_LIMITS.checks, sanitizeCheck)
        : [],
    topics:
      kind === 'tag' ? list(v.topics, JUDGE_LIMITS.topics, sanitizeTopic) : [],
    rules: textList(v.rules, JUDGE_LIMITS.rules),
    examples: list(v.examples, JUDGE_LIMITS.examples, (x, i) =>
      sanitizeExample(kind, x, i)
    ),
    bar: clampBar(v.bar, kind === 'score' ? 60 : PASS_AT),
    model: isJudgeModel(v.model)
      ? v.model
      : (RETIRED_MODELS[String(v.model)] ?? DEFAULT_JUDGE_MODEL),
  };
  if (kind === 'score' && !config.criteria.length) return null;
  if (kind === 'check' && !config.checks.length) return null;
  if (kind === 'tag' && !config.topics.length) return null;
  return config;
};

/* ---- Results ------------------------------------------------------------ */

export type Sentiment = 'positive' | 'neutral' | 'negative';

/** What one judge made of one conversation. */
export interface JudgeResult {
  kind: JudgeKind;
  /** Out of 100: `rubric` and `score`. */
  score: number | null;
  /** Null for `tag`, which neither passes nor fails. */
  pass: boolean | null;
  criteria: { key: string; label: string; score: number; reason: string }[];
  checks: { name: string; failed: boolean; reason: string }[];
  topic: string | null;
  subtopic: string | null;
  sentiment: Sentiment | null;
  summary: string;
}

/** A `score` judge's total: the criteria summed, out of 100. */
export const criteriaTotal = (
  criteria: ScoreCriterion[],
  scores: Record<string, number>
): number => {
  const max = criteria.reduce((sum, c) => sum + Math.max(...c.scores), 0);
  if (!max) return 0;
  const got = criteria.reduce((sum, c) => sum + (scores[c.key] ?? 0), 0);
  return Math.round((got / max) * 100);
};

/** The allowed score nearest to what the model answered. */
export const nearestAllowed = (allowed: number[], value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return allowed[0];
  return allowed.reduce((best, s) =>
    Math.abs(s - n) < Math.abs(best - n) ? s : best
  );
};

/** "Topic › Sub" or "Topic". */
export const topicLabel = (topic: string | null, sub: string | null) =>
  topic ? (sub ? `${topic} › ${sub}` : topic) : '';

/** What the judge said, in the form a person scores in: 84, "fail", "Billing › Autopay". */
export const resultValue = (r: JudgeResult): JudgeExpected =>
  r.kind === 'tag'
    ? topicLabel(r.topic, r.subtopic)
    : r.kind === 'check'
      ? r.pass
        ? 'pass'
        : 'fail'
      : (r.score ?? 0);

/** Within this many points, a score judge agrees with a person — if they agree on pass or fail too. */
export const AGREE_POINTS = 10;

/**
 * Whether the judge agrees with what a person gave the same conversation.
 * Scores agree when both pass or both fail and they are within
 * `AGREE_POINTS`; checks when both pass or both fail; tags on the topic, and
 * on the subtopic when the person gave one.
 */
export const judgeAgrees = (
  config: Pick<JudgeConfig, 'kind' | 'bar'>,
  team: JudgeExpected,
  result: JudgeResult
): boolean => {
  if (config.kind === 'check') return team === (result.pass ? 'pass' : 'fail');
  if (config.kind === 'tag') {
    const [topic, sub] = String(team)
      .split('›')
      .map((s) => s.trim().toLowerCase());
    if ((result.topic ?? '').toLowerCase() !== topic) return false;
    return !sub || (result.subtopic ?? '').toLowerCase() === sub;
  }
  const teamScore = Number(team);
  const judged = result.score ?? 0;
  return (
    teamScore >= config.bar === judged >= config.bar &&
    Math.abs(teamScore - judged) <= AGREE_POINTS
  );
};

/** A short, stable fingerprint of a config, so a check knows what it judged with. */
export const configHash = (config: JudgeConfig): string => {
  const s = JSON.stringify(config);
  let h = 5381;
  for (let i = 0; i < s.length; i += 1)
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

/* ---- The judges a solution starts with ---------------------------------- */

/** The standard rubric's criteria, in the `score` shape the page edits. */
export const RUBRIC_CRITERIA: ScoreCriterion[] = CRITERIA.map((c) => ({
  key: c.key,
  label: c.label,
  short: c.short,
  group: c.group,
  definition: c.definition,
  scores: LEVELS,
  levels: [...c.levels],
  weight: c.weight,
}));

const sameCriterion = (a: ScoreCriterion, b: ScoreCriterion) =>
  a.key === b.key &&
  a.label === b.label &&
  a.short === b.short &&
  a.group === b.group &&
  a.definition === b.definition &&
  a.weight === b.weight &&
  JSON.stringify(a.levels) === JSON.stringify(b.levels);

export const isStandardRubric = (criteria: ScoreCriterion[]): boolean =>
  criteria.length === RUBRIC_CRITERIA.length &&
  criteria.every((c, i) => sameCriterion(c, RUBRIC_CRITERIA[i]));

/** The criteria a Quality score judge scores on: its own, or the standard five. */
export const rubricCriteriaOf = (config: JudgeConfig): ScoreCriterion[] =>
  config.kind === 'rubric' && config.criteria.length
    ? config.criteria
    : RUBRIC_CRITERIA;

/** In the rubric's own shape, for the scoring prompt. */
export const asRubricCriteria = (criteria: ScoreCriterion[]): Criterion[] =>
  criteria.map((c) => ({
    key: c.key,
    label: c.label,
    short: c.short || c.label,
    group: c.group,
    definition: c.definition,
    levels: c.levels?.length ? c.levels : ['', '', '', '', ''],
    weight: c.weight ?? 0,
  }));

/** What a verdict keeps: nothing for the standard five, else each one's weight. */
export const verdictCriteriaOf = (
  criteria: ScoreCriterion[]
): VerdictCriterion[] | undefined =>
  !criteria.length || isStandardRubric(criteria)
    ? undefined
    : criteria.map((c) => ({
        key: c.key,
        label: c.label,
        short: c.short || c.label,
        weight: c.weight ?? 0,
      }));

export const rubricConfig = (): JudgeConfig => ({
  kind: 'rubric',
  name: 'Quality score',
  criteria: [],
  checks: [],
  topics: [],
  rules: [],
  examples: [],
  bar: PASS_AT,
  model: DEFAULT_JUDGE_MODEL,
});

/**
 * Guardrail checks, from the scorer services' auto-fail judge with its
 * customer-specific carve-outs taken out. It passes unless a check clearly
 * fails; the "never" lists are what keep it from crying wolf.
 */
export const GUARDRAIL_CHECKS: JudgeCheck[] = [
  {
    name: 'Unprofessional conduct',
    fails: [
      'Profanity, insults or mockery aimed at the customer',
      'Argues with the customer (combative, not merely unhelpful)',
      'Disparages the company, its products or other customers',
    ],
    never: [
      'A brief, generic or unhelpful answer',
      'Not expressing empathy',
      'Saying it is a virtual assistant and can’t help with something',
    ],
  },
  {
    name: 'Harmful action',
    fails: [
      'Guidance that, if followed, would directly cost the customer money or coverage, or block something they explicitly asked to do (you must be able to quote it and name the harm)',
      'No reply at all to a direct question',
    ],
    never: [
      'Misunderstanding the question or answering the wrong one',
      'Not escalating, clarifying or offering alternatives when it might have',
      'Standard troubleshooting steps that didn’t solve it',
      'Repeating itself, or the conversation looping',
    ],
  },
  {
    name: 'Compliance & security',
    fails: [
      'Shares internal or proprietary information, or another customer’s data',
      'Instructions to get around a safeguard',
    ],
    never: [
      'Repeating details the customer gave',
      'Asking for account details to verify the customer',
      'Masked text such as [secure object removed]',
    ],
  },
  {
    name: 'Grammar & fluency',
    fails: ['Misspelled words', 'Broken sentences that hide the meaning'],
    never: [
      'Any capitalization in the transcript',
      'Words or list numbers broken by masking',
      'Informal phrasing or minor punctuation',
    ],
  },
];

export const guardrailsConfig = (): JudgeConfig => ({
  kind: 'check',
  name: 'Guardrails',
  criteria: [],
  checks: GUARDRAIL_CHECKS.map((c) => ({
    ...c,
    fails: [...c.fails],
    never: [...c.never],
  })),
  topics: [],
  rules: ['Emoji and a friendly sign-off are acceptable.'],
  examples: [],
  bar: PASS_AT,
  model: DEFAULT_JUDGE_MODEL,
});

/** What the rubric judge's auto-fail list reads like, for its page. */
export const RUBRIC_AUTO_FAIL = AUTO_FAIL;
